<template>
    <div class="graphics-view">
        <template v-if="graphicsData && (graphicsData.echarts || tableData.length)">
            <b-row>
                <!-- Gráfico de Pilares o Elementos -->
                <b-col cols="12" :xl="showElementChart ? '12' : '6'" class="mb-3 mb-xl-0">
                    <b-overlay :show="chartLoading" rounded="sm">
                        <div class="chart-wrapper h-100">
                            <!-- Gráfico por Pilar -->
                            <template v-if="showPillarChart">
                                <div class="chart-header">
                                    <h5 class="chart-title">
                                        Calificación promedio por pilar
                                    </h5>
                                    <b-button-group size="sm">
                                        <b-button :variant="showPillarChart ? 'primary' : 'outline-secondary'"
                                            @click="showPillarChart = true; showElementChart = false">
                                            Por Pilar
                                        </b-button>
                                        <b-button :variant="showElementChart ? 'primary' : 'outline-secondary'"
                                            @click="showElementChart = true; showPillarChart = false">
                                            Por Elemento
                                        </b-button>
                                    </b-button-group>
                                </div>
                                <div class="card p-0 h-100-minus-header">
                                    <div class="card-body p-0">
                                        <base-chart :options="pillarChartOptions" :height="chartHeight"
                                            @magic-type-changed="handleMagicTypeChange" />
                                    </div>
                                </div>
                            </template>

                            <!-- Gráfico por Elemento -->
                            <template v-if="showElementChart">
                                <div class="chart-header">
                                    <h5 class="chart-title">
                                        Calificación promedio por elemento
                                    </h5>
                                    <b-button-group size="sm">
                                        <b-button :variant="showPillarChart ? 'primary' : 'outline-secondary'"
                                            @click="showPillarChart = true; showElementChart = false">
                                            Por Pilar
                                        </b-button>
                                        <b-button :variant="showElementChart ? 'primary' : 'outline-secondary'"
                                            @click="showElementChart = true; showPillarChart = false">
                                            Por Elemento
                                        </b-button>
                                    </b-button-group>
                                </div>
                                <div class="card p-0 h-100-minus-header">
                                    <div class="card-body p-0">
                                        <div class="chart-scroll-container">
                                            <base-chart :options="elementChartOptions" :height="chartHeight"
                                                :style="{ minWidth: chartMinWidth }" />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </b-overlay>
                </b-col>

                <!-- Gráfica de Bradley -->
                <b-col v-if="validarPermisoComponente('cargar-datos-grafica-bradley') || bradleyData" cols="12" xl="6"
                    class="mb-3 mb-xl-0">
                    <b-overlay :show="chartLoading" rounded="sm">
                        <div class="chart-wrapper h-100">
                            <div class="chart-header">
                                <h5 class="chart-title">
                                    Modelo Bradley – Cultura de Seguridad
                                </h5>
                                <b-button v-if="validarPermisoComponente('cargar-datos-grafica-bradley')" size="sm"
                                    variant="outline-primary" class="ml-auto" title="Cargar archivo bradley.json"
                                    @click="showBradleyModal = true">
                                    <i class="fa fa-upload" aria-hidden="true"></i> Cargar JSON
                                </b-button>
                            </div>
                            <div class="card p-0 h-100-minus-header">
                                <div class="card-body p-0">
                                    <base-chart :options="bradleyChartOptions" :height="chartHeight" />
                                </div>
                            </div>
                        </div>
                    </b-overlay>
                </b-col>

                <!-- Tabla de Elementos (siempre visible) -->
                <b-col cols="12" xl="6">
                    <b-overlay :show="tableLoading" rounded="sm">
                        <pillar-table :table-data="tableData" :rating-definitions="ratingDefinitions" />
                    </b-overlay>
                </b-col>
            </b-row>
        </template>

        <template v-else-if="!chartLoading && !tableLoading">
            <div class="no-data-message">
                <p>No hay datos disponibles para mostrar gráficas</p>
            </div>
        </template>

        <!-- Modal de carga de datos Bradley -->
        <bradley-upload-modal :visible.sync="showBradleyModal" :diagnostic-id="diagnosticId" :company-id="companyId"
            @data-uploaded="handleBradleyDataUploaded" @close="showBradleyModal = false" />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { mapActions } from 'vuex';
import BaseChart from './BaseChart.vue';
import PillarTable from './PillarTable.vue';
import BradleyUploadModal from './BradleyUploadModal.vue';
import { barHorizontalStacked, lineAverage, bradleyChart } from '../../graphics';
import { validarPermisoComponente } from '@/plugins/acl';

export default {
    name: 'GraphicsView',
    components: {
        BaseChart,
        PillarTable,
        BradleyUploadModal
    },
    props: {
        loading: {
            type: Boolean,
            default: false
        },
        chartLoading: {
            type: Boolean,
            default: false
        },
        tableLoading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            elementChartHeight: '500px',
            chartMinWidth: '100%',
            windowWidth: window.innerWidth,
            chartHeight: '500px',
            showPillarChart: true,
            showElementChart: false,
            showBradleyModal: false,
            bradleyData: null,
            validarPermisoComponente: validarPermisoComponente,
        };
    },
    computed: {
        ...mapState('skudo/diagnostics', ['graphicsData']),

        diagnosticId() {
            return this.$route.params.id ? parseInt(this.$route.params.id) : null;
        },

        companyId() {
            return this.$route.query.company_id ? parseInt(this.$route.query.company_id) : null;
        },

        ratingDefinitions() {
            if (!this.graphicsData || !this.graphicsData.rating_definitions) {
                return [];
            }
            return this.graphicsData.rating_definitions;
        },

        tableData() {
            if (!this.graphicsData || !this.graphicsData.table_data) {
                return [];
            }
            return this.graphicsData.table_data;
        },

        elementChartOptions() {
            if (this.graphicsData && this.graphicsData.echarts && this.graphicsData.echarts.element_horizontal_stacked) {
                const prebuiltData = this.graphicsData.echarts.element_horizontal_stacked;
                const averages = this.calculateAverages();

                return barHorizontalStacked({
                    ...prebuiltData,
                    averages,
                    pillars: this.getPillarInfo()
                });
            }

            if (!this.tableData || !Array.isArray(this.tableData) || !this.tableData.length) {
                return {};
            }

            const flatElements = this.getFlatElements();
            const ratingCols = this.getRatingColumns();
            const yAxis = flatElements.map(e => e.name || e.title || '');
            const series = this.buildSeries(flatElements, ratingCols);
            const averages = this.calculateAverages();
            const pillars = this.getPillarInfo();

            return barHorizontalStacked({ yAxis, series, averages, pillars, ratingDefinitions: ratingCols });
        },

        pillarChartOptions() {
            if (!this.graphicsData || !this.graphicsData.by_pillar) {
                return {};
            }

            return lineAverage(this.graphicsData, this.ratingDefinitions);
        },

        bradleyChartOptions() {
            if (!this.bradleyData) {
                return {
                    title: {
                        text: 'No hay datos cargados',
                        subtext: 'Carga un archivo JSON para visualizar el modelo Bradley',
                        left: 'center',
                        top: 'center',
                        textStyle: {
                            fontSize: 16,
                            color: '#718096'
                        },
                        subtextStyle: {
                            fontSize: 12,
                            color: '#a0aec0'
                        }
                    }
                };
            }
            return bradleyChart(this.bradleyData);
        },
    },
    mounted() {
        this.updateChartHeight();
        window.addEventListener('resize', this.updateChartHeight);
        this.loadBradleyData();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateChartHeight);
    },
    methods: {
        ...mapActions('skudo/diagnostics', ['getBradleyData']),

        loadBradleyData() {
            if (!this.diagnosticId || !this.companyId) return;

            this.getBradleyData({
                diagnosticId: this.diagnosticId,
                companyId: this.companyId
            }).then(response => {
                if (response && response.data) {
                    this.bradleyData = response.data;
                }
            }).catch(() => {
                this.bradleyData = null;
            });
        },

        getFlatElements() {
            const flatElements = [];
            this.tableData.forEach(pillar => {
                if (pillar.elements && Array.isArray(pillar.elements)) {
                    pillar.elements.forEach(el => {
                        flatElements.push({
                            ...el,
                            pillarName: pillar.name || pillar.title
                        });
                    });
                }
            });
            return flatElements;
        },

        getPillarInfo() {
            const defaultColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#f97316'];
            const pillarInfo = [];
            let elementIndex = 0;

            this.tableData.forEach((pillar, pIndex) => {
                if (pillar.elements && Array.isArray(pillar.elements)) {
                    const elementsCount = pillar.elements.length;
                    pillarInfo.push({
                        name: pillar.name || pillar.title,
                        color: pillar.color || defaultColors[pIndex % defaultColors.length],
                        startIndex: elementIndex,
                        endIndex: elementIndex + elementsCount - 1,
                        count: elementsCount
                    });
                    elementIndex += elementsCount;
                }
            });

            return pillarInfo;
        },

        getRatingColumns() {
            return (this.ratingDefinitions && this.ratingDefinitions.length)
                ? this.ratingDefinitions
                : [
                    { id: 'legacy-0', name: 'No aplica', value: 0, color: '#6c757d' },
                    { id: 'legacy-1', name: 'Muy bajo', value: 1, color: '#dc3545' },
                    { id: 'legacy-2', name: 'Bajo', value: 2, color: '#fd7e14' },
                    { id: 'legacy-3', name: 'Medio', value: 3, color: '#ffc107' },
                    { id: 'legacy-4', name: 'Alto', value: 4, color: '#17a2b8' },
                    { id: 'legacy-5', name: 'Muy alto', value: 5, color: '#28a745' }
                ];
        },

        buildSeries(flatElements, ratingCols) {
            return ratingCols.map((rating, rIndex) => {
                const data = flatElements.map((el) => {
                    const ratings = el.ratings || {};
                    const stats = ratings.ratings || ratings;
                    const entry = stats[rating.id] || stats[`rating_${rIndex}`];
                    const percentage = entry ? (entry.percentage || 0) : 0;
                    return Number(percentage.toFixed ? percentage.toFixed(2) : percentage);
                });

                return {
                    name: rating.name || `Rating ${rIndex}`,
                    data,
                    stack: 'total'
                };
            });
        },

        calculateAverages() {
            const flatElements = this.getFlatElements();
            if (!flatElements.length) return [];

            const ratingCols = this.getRatingColumns();

            return flatElements.map(el => {
                const ratings = el.ratings || {};
                if (!ratings || !ratings.total) return '0.00';

                let sum = 0;
                const stats = ratings.ratings || ratings;

                ratingCols.forEach((rating, index) => {
                    const entry = stats[rating.id] || stats[`rating_${index}`];
                    if (entry) {
                        sum += (entry.count || 0) * (typeof rating.value === 'number' ? rating.value : index);
                    }
                });

                return (sum / ratings.total).toFixed(2);
            });
        },

        updateChartHeight() {
            const width = window.innerWidth;
            this.windowWidth = width;
            const numElements = this.tableData.reduce((count, pillar) => {
                return count + (pillar.elements ? pillar.elements.length : 0);
            }, 0);

            if (width < 768) {
                const heightPerElement = 65;
                const minHeight = 400;
                const maxHeight = 1500;
                const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, numElements * heightPerElement));
                this.elementChartHeight = `${calculatedHeight}px`;

                const widthPerElement = 170;
                const calculatedWidth = Math.max(1100, numElements * widthPerElement);
                this.chartMinWidth = `${calculatedWidth}px`;
            } else {
                this.elementChartHeight = '500px';
                const widthPerElement = 160;
                const calculatedWidth = numElements * widthPerElement;
                this.chartMinWidth = calculatedWidth > 1200 ? `${calculatedWidth}px` : '100%';
            }
        },

        handleMagicTypeChange(type) {
            this.chartType = type === 'stack' ? 'stack' : 'bar';
        },

        handleBradleyDataUploaded() {
            this.loadBradleyData();
        }
    }
};
</script>

<style scoped>
.graphics-view {
    width: 100%;
    background-color: transparent;
}

.chart-toggle {
    display: flex;
    justify-content: flex-start;
}

.chart-toggle .btn-group {
    border-radius: 8px;
    overflow: hidden;
}

.chart-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    max-height: none;
}

.h-100-minus-header {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.chart-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    justify-content: space-between;
}

.chart-title {
    position: relative;
    margin-bottom: 0;
    font-size: 18px;
    font-weight: 800;
    color: #1B212E;
    z-index: 1;
    display: flex;
    align-items: center;
}

.chart-title i {
    color: #666;
}

.card {
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 25px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.card-body {
    padding: 1rem !important;
    font-family: 'Poppins', sans-serif;
    font-weight: 300;
    color: #1B212E;
}

.chart-scroll-container {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
}

.chart-scroll-container::-webkit-scrollbar {
    height: 8px;
}

.chart-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.chart-scroll-container::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
}

.chart-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

.no-data-message {
    text-align: center;
    color: #718096;
    min-height: 300px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: none;
    border-radius: 16px;
    box-shadow: none;
}

.no-data-message p {
    margin: 0;
    font-size: 14px;
}

@media (max-width: 767px) {
    .chart-title {
        font-size: 16px;
    }

    .card {
        border-radius: 20px;
    }
}

@media (max-width: 575px) {
    .chart-title {
        font-size: 14px;
    }

    .chart-header {
        margin-bottom: 0.5rem;
    }
}
</style>
